/**
 * SINGLE SOURCE OF TRUTH for plans, quotas and credit packs.
 *
 * The marketing site renders copy from this file. The product
 * (app.kitagency.co) must import the SAME config for server-side feature
 * gating. Never restate a number anywhere else in the codebase.
 */

export type TierId = 'starter' | 'growth' | 'scale';
/** Includes the anchor, which is not a sellable Tier. */
export type PlanId = TierId | 'enterprise';

/**
 * Every gateable capability in the product, in display order.
 *
 * The first three are the floor: every plan is a fast managed site with a CMS
 * the owner cannot break and a place for leads to land. Everything after them
 * is what a visitor is actually buying when they move up a tier, so the rows
 * are ordered to make the Starter -> Growth jump read as one decision rather
 * than a scatter of small ones.
 */
export const FEATURE_KEYS = [
  'managedSite',
  'localSeo',
  'monthlyAudit',
  'cmsHosting',
  'leadCrm',
  'analytics',
  'aiChatbot',
  'bookingEngine',
  'heatmaps',
  'abTesting',
  'claudeMcp',
  'personalisation',
  'growthLab',
  'referralProgram',
] as const;

export type FeatureKey = (typeof FEATURE_KEYS)[number];

export const FEATURE_LABELS: Record<FeatureKey, string> = {
  managedSite: 'Fast, mobile-friendly managed site & hosting',
  localSeo: 'Local SEO on the site we run',
  monthlyAudit: 'Monthly growth audit',
  cmsHosting: 'Unbreakable CMS (field-locked)',
  leadCrm: 'Lead CRM & standard forms',
  analytics: 'Visitor & lead analytics',
  aiChatbot: '24/7 PDF-trained AI chatbot',
  bookingEngine: 'Native booking engine',
  heatmaps: 'Heatmaps & scroll depth',
  abTesting: 'Statistical A/B testing',
  claudeMcp: 'Claude connector (products & blog posts)',
  personalisation: 'Dynamic personalisation',
  growthLab: 'Growth Lab tools & freebies',
  referralProgram: 'Customer referral program',
};

/**
 * One plain sentence per row of the comparison table, saying what the thing
 * actually does. Each is a statement about the shipped product, so check it
 * against the app before changing it.
 */
export const FEATURE_NOTES: Record<FeatureKey, string> = {
  managedSite: 'We build the site, host it and keep it fast on phones, tablets and desktops.',
  localSeo: 'Google Business Profile upkeep, service-area pages and structured data, on the website we host for you.',
  monthlyAudit: 'Every month we review what came in, what leaked and what to fix next, and tell you in plain words.',
  cmsHosting: 'Edit text and images from a dashboard. The layout is locked, so you cannot break it.',
  leadCrm: 'Every enquiry lands in one list with a status and a score. Export to CSV any time.',
  analytics: 'Visits, pages and sources, plus lead and conversion figures.',
  aiChatbot: 'Answers visitors from the documents you upload, at any hour. If the answer is not in them, it says so.',
  bookingEngine: 'Visitors request a time. You confirm or decline it from your dashboard.',
  heatmaps: 'See where visitors click and how far down each page they scroll.',
  abTesting: 'Test two versions of a headline or button and see when the difference is real.',
  claudeMcp:
    'Connect KIT to your own Claude account to add products and draft blog posts from a chat. Everything arrives as a draft for you to approve.',
  personalisation: 'The site changes its headline, offer and proof for each visitor: their city, the ad or search that brought them, or their industry.',
  growthLab: 'Interactive assessments, calculators and gated guides that turn a visitor into a lead with an email address.',
  referralProgram:
    'Give happy customers and partners their own referral link. Every enquiry they send is tracked back to them, so you know who to thank or reward.',
};

/**
 * Whether the app's plan gate for the Claude connector is KNOWN to be live in
 * production (Growth, Scale and Enterprise allowed; Starter refused with
 * MCP_NOT_INCLUDED). The gate lives in the app repo and this site cannot see
 * production, so until the owner has checked it and set
 * NEXT_PUBLIC_MCP_GATE_VERIFIED=true, the site says which plans OFFER the
 * connector and never states that Starter is refused it.
 */
export const MCP_GATE_VERIFIED = process.env.NEXT_PUBLIC_MCP_GATE_VERIFIED === 'true';

/** The capabilities a plan does not include, without any restriction the app is not yet known to enforce. */
export function missingFeatureKeys(features: Record<FeatureKey, boolean>): FeatureKey[] {
  return FEATURE_KEYS.filter((k) => !features[k] && (k !== 'claudeMcp' || MCP_GATE_VERIFIED));
}

export type Tier = {
  id: TierId;
  name: string;
  /** The plain-English name for what this plan IS, shown above the price. */
  positioning: string;
  /** Who it is for. One sentence, no hedging. */
  audience: string;
  /** Monthly price in USD. `null` means custom / quoted on a call. */
  price: number | null;
  priceLabel: string;
  tagline: string;
  /** One sentence on what the owner gets out of it. Plain, no ROI claim. */
  outcome: string;
  /** The five or six things that decide the purchase. Everything else lives in the comparison table. */
  highlights: string[];
  /** How the plan grows the business, in one owner-level sentence. Not a guaranteed result. */
  growth: string;
  /** Savings-model row ids (lib/savings.ts) whose separate tools this plan replaces. */
  replaces: string[];
  /** `null` means negotiated per contract. */
  seats: number | null;
  seatsLabel: string;
  aiCredits: number;
  mailCredits: number;
  features: Record<FeatureKey, boolean>;
  /**
   * Things that are not a simple on/off capability: delivered work, setup, and
   * support promises. Listed on the card under the capability list.
   */
  extras: string[];
  /** Shown as "new" chip on the pricing matrix. */
  highlightFeature?: FeatureKey;
  /** The exact `highlights` entry that carries the NEW chip on the card. */
  newHighlight?: string;
  popular?: boolean;
  ctaLabel: string;
};

const FLOOR = {
  managedSite: true,
  localSeo: true,
  monthlyAudit: true,
  cmsHosting: true,
  leadCrm: true,
  analytics: true,
} as const;

export const TIERS: Tier[] = [
  {
    id: 'starter',
    name: 'Starter',
    positioning: 'Look professional. Get found nearby.',
    audience:
      'Solo operators who need a fast, bulletproof mobile site that does not break.',
    price: 199,
    priceLabel: '$199',
    tagline: 'A site that loads fast, stays fixed, gets found nearby and puts every enquiry in one place.',
    outcome: 'A fast, mobile-friendly site that shows up in local search and never loses an enquiry.',
    highlights: [
      'Mobile-friendly website, built and hosted by us',
      'Local SEO: Google Business Profile, service-area pages, structured data',
      'Edit words and photos yourself. The layout cannot break',
      'Every enquiry in one lead CRM, with email alerts',
      'Visitor and lead analytics',
      'AI copilot that drafts your content',
      'A monthly growth audit, in plain words',
    ],
    growth: 'You stop losing the enquiries that used to vanish in an inbox, and nearby customers start finding you.',
    replaces: ['build', 'crm', 'cms'],
    seats: 1,
    seatsLabel: '1 admin seat',
    aiCredits: 25_000,
    mailCredits: 5_000,
    features: {
      ...FLOOR,
      aiChatbot: false,
      bookingEngine: false,
      heatmaps: false,
      abTesting: false,
      claudeMcp: false,
      personalisation: false,
      growthLab: false,
      referralProgram: false,
    },
    extras: [
      'Managed hosting, and we keep it fast',
      'Local SEO on the site we run for you',
      'Field-locked CMS: change the words and pictures, never the layout',
      'Lead capture forms with email notifications',
      'Lead CRM with one-click CSV export',
      'Basic visitor analytics',
      'Owner copilot for drafting content',
      'Monthly growth audit',
    ],
    ctaLabel: 'Start your 30 free days',
  },
  {
    id: 'growth',
    name: 'Growth',
    positioning: 'A front desk that never sleeps.',
    audience:
      'Service businesses running ads or taking evening traffic, losing the enquiries nobody was awake to answer.',
    price: 499,
    priceLabel: '$499',
    tagline: 'Answers at 3am, takes the booking request, and puts every lead in front of you.',
    outcome: 'Answers questions at any hour, takes booking requests, and shows you exactly where visitors drop off.',
    highlights: [
      'Everything in Starter',
      '24/7 AI assistant trained on your own documents',
      'Booking requests you confirm from your phone',
      'Heatmaps: see where visitors click and where they stop',
      'A/B testing that tells you when a change really worked',
      'Claude connector for products and blog posts',
    ],
    growth: 'The 9pm enquiry gets an answer and a booking request, instead of going to the next company on Google.',
    replaces: ['build', 'crm', 'cms', 'chatbot', 'heatmaps', 'abtesting', 'booking', 'scoring'],
    seats: 3,
    seatsLabel: '3 admin seats',
    aiCredits: 100_000,
    mailCredits: 20_000,
    features: {
      ...FLOOR,
      aiChatbot: true,
      bookingEngine: true,
      heatmaps: true,
      abTesting: true,
      claudeMcp: true,
      personalisation: false,
      growthLab: false,
      referralProgram: false,
    },
    extras: ['Everything in Starter'],
    highlightFeature: 'aiChatbot',
    newHighlight: '24/7 AI assistant trained on your own documents',
    popular: true,
    ctaLabel: 'Start your 30 free days',
  },
  {
    id: 'scale',
    name: 'Scale',
    positioning: 'A website that turns more visitors into leads.',
    audience:
      'Established companies with a bigger team, more traffic and more than one kind of customer.',
    price: 999,
    priceLabel: '$999',
    tagline: 'Everything in Growth, plus tools that turn visitors and happy customers into leads.',
    outcome: 'More visitors become leads through guides and calculators, and happy customers send you the next one.',
    highlights: [
      'Everything in Growth',
      'Referral program: customers share their own link, and every enquiry they send is tracked',
      'Growth Lab: assessments and calculators that capture leads',
      'Freebies: gated guides that collect emails for you',
      '10 admin seats, 300,000 AI credits and 50,000 mail credits a month',
      'Priority one-to-one strategy and technical support',
    ],
    growth: 'A visitor who is not ready yet leaves their email for a guide, and a happy customer sends a neighbour with their own link.',
    replaces: ['build', 'crm', 'cms', 'chatbot', 'heatmaps', 'abtesting', 'booking', 'scoring', 'email', 'growthlab', 'referral'],
    seats: 10,
    seatsLabel: '10 admin seats',
    aiCredits: 300_000,
    mailCredits: 50_000,
    features: {
      ...FLOOR,
      aiChatbot: true,
      bookingEngine: true,
      heatmaps: true,
      abTesting: true,
      claudeMcp: true,
      personalisation: false,
      growthLab: true,
      referralProgram: true,
    },
    extras: ['Everything in Growth', 'Customer referral program', 'Growth Lab tools and freebies', 'Priority one-to-one strategy and technical support'],
    highlightFeature: 'referralProgram',
    newHighlight: 'Referral program: customers share their own link, and every enquiry they send is tracked',
    ctaLabel: 'Start your 30 free days',
  },
];

/**
 * Software plans never carry a setup fee and start with 30 days at no charge.
 * Said once here so every card, FAQ and the assistant say the same thing.
 */
export const SOFTWARE_TERMS = {
  freeDays: 30,
  line: 'No setup fee. Your first 30 days are free, then month to month.',
  short: 'First 30 days free',
} as const;

/**
 * The anchor, deliberately NOT a member of TIERS.
 *
 * It exists so $999 has something above it to be measured against, and so a
 * five-location franchise or a white-label agency has somewhere to land. It is
 * not a column in the comparison matrix, not an Offer in the structured data,
 * and not a row in llms.txt, because there is no price to compare, tick or
 * quote. A fourth column of "custom" against nine feature rows is noise.
 */
export const ENTERPRISE = {
  id: 'enterprise' as const,
  name: 'Enterprise',
  positioning: 'Custom',
  priceLabel: 'Custom',
  blurb:
    'Multi-franchise networks, custom API routing, and a dedicated SLA. Priced against what you actually run.',
  /** The one capability only Enterprise has. Carries the "New" chip. */
  newHighlight: 'Dynamic personalisation: a different headline, offer and proof for each city, source or industry',
  newFeature: 'personalisation' as FeatureKey,
  ctaLabel: 'Talk to sales',
  ctaHref: '/contact?tier=enterprise',
} as const;

/** Plan name for a `?tier=` value, including the anchor that is not a Tier. */
export function getPlanName(id: string | undefined | null): string | undefined {
  if (id === ENTERPRISE.id) return ENTERPRISE.name;
  return getTier(id)?.name;
}

export const TIER_IDS = TIERS.map((t) => t.id);

export function getTier(id: string | undefined | null): Tier | undefined {
  return TIERS.find((t) => t.id === id);
}

/**
 * One-off top-up packs. There is no self-service checkout: a pack is added to
 * your account by us and invoiced with the next payment, so every button here
 * routes to the contact page rather than a payment processor.
 */
export type CreditPack = {
  id: string;
  credits: number;
  price: number;
  best?: boolean;
};

/** Effective unit economics, derived — never hand-written. */
export function pricePerThousand(pack: CreditPack): number {
  return (pack.price / pack.credits) * 1000;
}

export const AI_CREDIT_PACKS: CreditPack[] = [
  { id: 'ai-50k', credits: 50_000, price: 25 },
  { id: 'ai-250k', credits: 250_000, price: 100 },
  { id: 'ai-1m', credits: 1_000_000, price: 300, best: true },
];

export const MAIL_CREDIT_PACKS: CreditPack[] = [
  { id: 'mail-5k', credits: 5_000, price: 5 },
  { id: 'mail-25k', credits: 25_000, price: 20 },
  { id: 'mail-100k', credits: 100_000, price: 60, best: true },
];

export const COMPANY = {
  brand: 'KIT Agency',
  entity: 'Karki Innovative Technologies',
  operatedBy: 'KIT Agency (kitagency.co) is operated by Karki Innovative Technologies',
  supportEmail: 'sales@kitagency.co',
  appUrl: process.env.NEXT_PUBLIC_APP_URL || 'https://app.kitagency.co',
  loginUrl: `${process.env.NEXT_PUBLIC_APP_URL || 'https://app.kitagency.co'}/login`,
  /**
   * We invoice directly and take payment by bank transfer. Wise and Remitly
   * are named because they are the cheapest and fastest routes for most
   * countries sending to Nepal; any bank transfer works.
   */
  billing: {
    method: 'Bank transfer',
    services: 'Wise or Remitly',
    summary: 'We invoice you and you pay by bank transfer. Wise and Remitly are usually cheapest.',
  },
} as const;

export const nf = new Intl.NumberFormat('en-US');
